/**
 * Per-bib SMS subscription client.
 * Posts to the backend public endpoint that records the spectator opt-in
 * and sends a confirmation SMS via Twilio.
 */

const BACKEND_URL = import.meta.env.VITE_BACKEND_API_URL?.replace(/\/$/, '') ?? '';

export interface SubscribeArgs {
  slug: string;
  bibNumber: string;
  name: string;
  phone: string;
  agreedToTerms: boolean;
}

export interface SubscribeResult {
  ok: boolean;
  message?: string;
  error?: string;
}

export async function subscribeToBib(args: SubscribeArgs): Promise<SubscribeResult> {
  if (!BACKEND_URL) {
    return {
      ok: false,
      error: 'SMS signup is not configured for this site (missing backend URL).',
    };
  }

  let response: Response;
  try {
    response = await fetch(
      `${BACKEND_URL}/api/races/${encodeURIComponent(args.slug)}/bib-subscriptions`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bib_number: args.bibNumber.trim(),
          name: args.name.trim(),
          phone_number: args.phone.trim(),
          agreed_to_terms: args.agreedToTerms,
        }),
      },
    );
  } catch (err) {
    return {
      ok: false,
      error: 'Network error — please check your connection and try again.',
    };
  }

  let payload: { error?: string; message?: string } = {};
  try {
    payload = (await response.json()) as { error?: string; message?: string };
  } catch {
    // Empty body — ignore
  }

  if (!response.ok) {
    return {
      ok: false,
      error: payload.error ?? `Signup failed (${response.status}).`,
    };
  }

  return {
    ok: true,
    message: payload.message,
  };
}
